import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRosteringAccess } from '../hooks/useRosteringAccess';
import type { Employee } from '../../admin/types/Employee';
import { Spinner } from '../../../shared/components';
import { TimeInput24 } from '../../../shared/components/TimeInput24';
import { useToast } from '../../notifications';
import {
  listRosterEmployees,
  listRosterRules,
  saveEmployeeWeekTab,
} from '../services/rosteringService';
import type { RosterScheduleRule, RosterViewMode, WeekDayState, WeekScheduleTab } from '../types/Rostering';
import {
  applyRosterSaveResult,
  getMonthDays,
  getWeekDays,
  startOfMonth,
  startOfWeekMonday,
  toDateKey,
} from '../utils/rosterUtils';
import {
  buildEmployeeScheduleUrl,
  buildRosterListUrl,
  parseMonthParam,
  parseRosterViewMode,
  parseScheduleTabParam,
  toMonthKey,
} from '../utils/rosterViewUtils';
import {
  addTabEntry,
  buildWeekDayStates,
  calculateTabWeekHours,
  getTabEntries,
  isTabDayActive,
  parseWeekStartParam,
  removeTabEntry,
  setTabDayActive,
  updateTabEntry,
} from '../utils/weekScheduleUtils';
import { STAFF_SCHEDULE_STATUSES } from '../utils/scheduleStatusUtils';
import type { StaffScheduleStatus } from '../types/Rostering';
import { WeekRangePicker } from './WeekRangePicker';
import { ViewModeToggle } from './ViewModeToggle';
import headerStyles from './RosterHeader.module.css';
import styles from './EmployeeWeekSchedulePage.module.css';

const serializeWeekDays = (days: WeekDayState[]): string => JSON.stringify(days);

export const EmployeeWeekSchedulePage: React.FC = () => {
  const { employeeId = '' } = useParams<{ employeeId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation(['rostering', 'common']);
  const { showToast } = useToast();
  const { canManageAll, canEditEmployee, canViewEmployee, myEmployeeId } = useRosteringAccess();
  const canManage = canEditEmployee(employeeId);

  const viewMode = parseRosterViewMode(searchParams.get('view'));
  const weekParam = searchParams.get('week');
  const weekStart = React.useMemo(
    () => startOfWeekMonday(parseWeekStartParam(weekParam) ?? new Date()),
    [weekParam],
  );
  const monthStart = React.useMemo(
    () => parseMonthParam(searchParams.get('month'), weekStart),
    [searchParams, weekStart],
  );
  const periodDays = React.useMemo(
    () => (viewMode === 'month' ? getMonthDays(monthStart, t) : getWeekDays(weekStart, t)),
    [monthStart, t, viewMode, weekStart],
  );

  const [activeTab, setActiveTab] = React.useState<WeekScheduleTab>(
    () => parseScheduleTabParam(searchParams.get('tab')),
  );
  const [employees, setEmployees] = React.useState<Employee[]>([]);
  const [rules, setRules] = React.useState<RosterScheduleRule[]>([]);
  const [dayStates, setDayStates] = React.useState<WeekDayState[]>([]);
  const [baseline, setBaseline] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    setActiveTab(parseScheduleTabParam(searchParams.get('tab')));
  }, [searchParams]);

  const isDirty = baseline !== '' && serializeWeekDays(dayStates) !== baseline;

  const syncWeekParam = React.useCallback((nextWeekStart: Date) => {
    const next = new URLSearchParams(searchParams);
    next.set('view', 'week');
    next.set('week', toDateKey(nextWeekStart));
    next.delete('month');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const syncMonthParam = React.useCallback((nextMonthStart: Date) => {
    const next = new URLSearchParams(searchParams);
    next.set('view', 'month');
    next.set('month', toMonthKey(nextMonthStart));
    next.delete('week');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const changeViewMode = (nextView: RosterViewMode) => {
    if (isDirty && !window.confirm(t('unsavedWeekChanges'))) {
      return;
    }

    navigate(buildEmployeeScheduleUrl({
      employeeId,
      view: nextView,
      weekStart,
      monthStart: nextView === 'month' ? monthStart : startOfMonth(weekStart),
    }), { replace: true });
  };

  const renderDayCard = (day: WeekDayState, tab: WeekScheduleTab) => {
    const isActive = isTabDayActive(day, tab);
    const entries = getTabEntries(day, tab);
    const statusLabel = isActive ? '' : (tab === 'schedule' ? t('off') : t('notOnCall'));

    return (
      <section
        key={`${tab}-${day.dayKey}`}
        className={`${styles.dayCard} ${isActive ? styles.dayCardActive : styles.dayCardInactive}`}
      >
        <div className={styles.dayHeader}>
          <label className={styles.dayToggle}>
            <input
              type="checkbox"
              checked={isActive}
              disabled={!canManage}
              onChange={(event) => {
                setDayStates((current) => current.map((item) => (
                  item.dayKey === day.dayKey
                    ? setTabDayActive(item, tab, event.target.checked)
                    : item
                )));
              }}
            />
            <span className={styles.dayTitle}>{day.shortLabel}</span>
            {!isActive ? <span className={styles.dayStatus}>– {statusLabel}</span> : null}
          </label>

          {canManage && isActive ? (
            <button
              type="button"
              className={styles.addButton}
              onClick={() => {
                setDayStates((current) => current.map((item) => (
                  item.dayKey === day.dayKey ? addTabEntry(item, tab) : item
                )));
              }}
            >
              {t('add', { ns: 'common' })}
            </button>
          ) : null}
        </div>

        {isActive ? (
          <div className={styles.entryList}>
            {entries.map((entry) => (
              <div
                key={entry.key}
                className={`${styles.entryRow} ${tab === 'onCall' ? styles.entryRowOnCall : ''}`}
              >
                <div className={styles.timeRange}>
                  <TimeInput24
                    className={`text-input ${styles.compactInput} ${styles.timeInput}`}
                    value={entry.startTime}
                    disabled={!canManage}
                    onChange={(nextValue) => {
                      setDayStates((current) => current.map((item) => (
                        item.dayKey === day.dayKey
                          ? updateTabEntry(item, tab, entry.key, { startTime: nextValue })
                          : item
                      )));
                    }}
                  />
                  <span className={styles.timeSeparator}>–</span>
                  <TimeInput24
                    className={`text-input ${styles.compactInput} ${styles.timeInput}`}
                    value={entry.endTime}
                    disabled={!canManage}
                    onChange={(nextValue) => {
                      setDayStates((current) => current.map((item) => (
                        item.dayKey === day.dayKey
                          ? updateTabEntry(item, tab, entry.key, { endTime: nextValue })
                          : item
                      )));
                    }}
                  />
                </div>

                {tab === 'schedule' ? (
                  <select
                    className={`text-input ${styles.compactInput} ${styles.statusSelect}`}
                    value={entry.scheduleStatus}
                    disabled={!canManage}
                    onChange={(event) => {
                      setDayStates((current) => current.map((item) => (
                        item.dayKey === day.dayKey
                          ? updateTabEntry(item, tab, entry.key, {
                            scheduleStatus: event.target.value as StaffScheduleStatus,
                          })
                          : item
                      )));
                    }}
                  >
                    {STAFF_SCHEDULE_STATUSES.map((status) => (
                      <option key={status} value={status}>
                        {t(`scheduleStatuses.${status}`)}
                      </option>
                    ))}
                  </select>
                ) : null}

                {canManage ? (
                  <button
                    type="button"
                    className={styles.deleteEntryButton}
                    aria-label={t('delete', { ns: 'common' })}
                    onClick={() => {
                      setDayStates((current) => current.map((item) => (
                        item.dayKey === day.dayKey
                          ? removeTabEntry(item, tab, entry.key)
                          : item
                      )));
                    }}
                  >
                    ×
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
      </section>
    );
  };

  const loadData = React.useCallback(async () => {
    setLoading(true);

    try {
      const [nextEmployees, nextRules] = await Promise.all([
        listRosterEmployees(),
        listRosterRules(),
      ]);
      setEmployees(nextEmployees);
      setRules(nextRules);

      const nextDayStates = buildWeekDayStates(periodDays, nextRules, employeeId, t);
      setDayStates(nextDayStates);
      setBaseline(serializeWeekDays(nextDayStates));
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToLoad'), 'error');
    } finally {
      setLoading(false);
    }
  }, [employeeId, periodDays, showToast, t]);

  React.useEffect(() => {
    if (!employeeId) {
      return;
    }

    if (canViewEmployee(employeeId)) {
      return;
    }

    if (myEmployeeId) {
      navigate(buildEmployeeScheduleUrl({
        employeeId: myEmployeeId,
        view: viewMode,
        weekStart,
        monthStart,
      }), { replace: true });
      return;
    }

    navigate('/unauthorized', { replace: true });
  }, [canViewEmployee, employeeId, monthStart, myEmployeeId, navigate, viewMode, weekStart]);

  React.useEffect(() => {
    void loadData();
  }, [loadData]);

  const changeWeek = (nextWeekStart: Date) => {
    if (isDirty && !window.confirm(t('unsavedWeekChanges'))) {
      return;
    }

    syncWeekParam(nextWeekStart);
  };

  const changeMonth = (nextMonthStart: Date) => {
    if (isDirty && !window.confirm(t('unsavedWeekChanges'))) {
      return;
    }

    syncMonthParam(nextMonthStart);
  };

  const changeEmployee = (nextEmployeeId: string) => {
    if (!nextEmployeeId || nextEmployeeId === employeeId || !canViewEmployee(nextEmployeeId)) {
      return;
    }

    if (isDirty && !window.confirm(t('unsavedWeekChanges'))) {
      return;
    }

    navigate(buildEmployeeScheduleUrl({
      employeeId: nextEmployeeId,
      view: viewMode,
      weekStart,
      monthStart,
    }));
  };

  const handleSave = async () => {
    if (!employeeId) {
      return;
    }

    for (const day of dayStates) {
      if (!isTabDayActive(day, activeTab)) {
        continue;
      }

      for (const entry of getTabEntries(day, activeTab)) {
        if (!entry.startTime || !entry.endTime) {
          showToast(t('timesRequired'), 'error');
          return;
        }
      }
    }

    setSaving(true);

    try {
      const result = await saveEmployeeWeekTab(employeeId, dayStates, activeTab, rules);
      const nextRules = applyRosterSaveResult(rules, result);
      setRules(nextRules);

      const nextDayStates = buildWeekDayStates(periodDays, nextRules, employeeId, t);
      setDayStates(nextDayStates);
      setBaseline(serializeWeekDays(nextDayStates));
      showToast(t('weekSaved'), 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToSave'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!employeeId) {
    return null;
  }

  return (
    <div className={styles.page}>
      <div className={headerStyles.pageHeader}>
        <div className={headerStyles.topBar}>
        <div className={headerStyles.topBarLeading}>
          <button type="button" className={styles.backLink} onClick={() => navigate(buildRosterListUrl({ view: viewMode, weekStart, monthStart }))}>
            ← {t('backToRoster')}
          </button>

          <ViewModeToggle value={viewMode} onChange={changeViewMode} />

          {canManageAll ? (
            <select
              className={styles.employeeSelect}
              value={employeeId}
              onChange={(event) => changeEmployee(event.target.value)}
              aria-label={t('employee')}
            >
              {employees.map((item) => (
                <option key={item.id} value={item.id}>{item.name}</option>
              ))}
            </select>
          ) : null}

          <WeekRangePicker
            mode={viewMode}
            weekStart={weekStart}
            monthStart={monthStart}
            onWeekChange={changeWeek}
            onMonthChange={changeMonth}
            className={headerStyles.weekControls}
          />
        </div>

        {canManage ? (
          <div className={headerStyles.topBarTrailing}>
            <button type="button" className="btn btn-primary" onClick={() => void handleSave()} disabled={saving || !isDirty}>
              {saving ? t('saving', { ns: 'common' }) : t('saveWeek')}
            </button>
          </div>
        ) : null}
        </div>

        <div className={styles.tabsRow}>
          <div className={styles.tabs}>
            <button
              type="button"
              className={`${styles.tab} ${activeTab === 'schedule' ? styles.tabActive : ''}`}
              onClick={() => {
                setActiveTab('schedule');
                const next = new URLSearchParams(searchParams);
                next.set('tab', 'schedule');
                setSearchParams(next, { replace: true });
              }}
            >
              {t('scheduleTab')}
            </button>
            <button
              type="button"
              className={`${styles.tab} ${activeTab === 'onCall' ? styles.tabActive : ''}`}
              onClick={() => {
                setActiveTab('onCall');
                const next = new URLSearchParams(searchParams);
                next.set('tab', 'onCall');
                setSearchParams(next, { replace: true });
              }}
            >
              {t('onCallTab')}
            </button>
          </div>

          {!loading ? (
            <p className={styles.hoursSummary}>
              {t(activeTab === 'schedule' ? 'scheduleHoursTotal' : 'onCallHoursTotal', {
                hours: calculateTabWeekHours(dayStates, activeTab),
              })}
            </p>
          ) : null}
        </div>
      </div>

      <div className={styles.formBody}>
        {loading ? (
          <div className={styles.loadingState}>
            <Spinner text={t('loading')} />
          </div>
        ) : (
          <div className={styles.dayListWrap}>
            <div className={`${styles.dayList} ${viewMode === 'month' ? styles.dayListMonth : ''}`}>
              {dayStates.map((day) => renderDayCard(day, activeTab))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
